import { IAttainsWatershed } from '../types/attains';
import {
  getCachedWatershed,
  setCachedWatershed,
  getCachedCrosswalk,
  setCachedCrosswalk,
} from './attainsCache';

const TIMEOUT_MS = 60000;
const ATTAINS_API = `${process.env.REACT_APP_CERES_URL}/api/attains`;

// The ECHO CWA facility endpoint uses this placeholder when FacDerivedWBD is
// missing; there is no real HUC-12 to query ATTAINS with in that case.
const HUC12_PLACEHOLDER = '[HUC-12 unavailable]';

async function fetchWithTimeout(url: string): Promise<any> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: ctrl.signal });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  } catch (e: any) {
    if (e.name === 'AbortError') throw new Error('Request timed out');
    throw e;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Fetch the assembled ATTAINS watershed status for a HUC-12, via the backend
 * proxy (which does the per-AU fan-out and Cause-filtering server-side).
 * Results are cached in IndexedDB for 30 days. Returns `available: false` when
 * the HUC-12 is unresolved or ATTAINS has no data for it.
 */
export async function fetchAttainsWatershed(huc12: string): Promise<IAttainsWatershed> {
  if (!huc12 || huc12 === HUC12_PLACEHOLDER || !/^\d{12}$/.test(huc12)) {
    return { huc12, available: false, reason: 'Receiving watershed (HUC-12) unresolved for this facility' };
  }

  const cached = await getCachedWatershed(huc12);
  if (cached) return cached;

  const data: IAttainsWatershed = await fetchWithTimeout(
    `${ATTAINS_API}/watershed?huc=${encodeURIComponent(huc12)}`
  );
  await setCachedWatershed(huc12, data);
  return data;
}

/**
 * Fetch the EPA DMR parameter → pollutant-category crosswalk (code → categories),
 * via the backend proxy. Cached in IndexedDB for 90 days.
 */
export async function fetchDmrCrosswalk(): Promise<Record<string, string[]>> {
  const cached = await getCachedCrosswalk();
  if (cached) return cached;

  const data: { map: Record<string, string[]> } = await fetchWithTimeout(
    `${ATTAINS_API}/dmr-crosswalk`
  );
  const map = data?.map ?? {};
  await setCachedCrosswalk(map);
  return map;
}
